import { createSolarApi } from '../../core/api/solarApi.js';
import { createFinanceApi, normalizeFinance } from '../../core/api/financeApi.js';
import { formatCurrencyGBP, formatNumber } from '../../core/utils/format.js';
import { debounce } from '../../core/utils/debounce.js';
import { getQueryParam } from '../../core/utils/params.js';
import {
    getTotalCost,
    getAnnualSavings,
    getPackageName,
    getPanelCount,
    getBatteryCount
} from './transforms.js';

function resolveConfig(root) {
    const baseUrl = root?.dataset?.apiBase || root?.dataset?.baseUrl || 'https://api.dwellow.co.uk';
    const siteKey = root?.dataset?.siteKey || '';
    const mapsKey = root?.dataset?.mapsKey || '';
    const scenarioId =
        getQueryParam('scenarioId') ||
        getQueryParam('jobId') ||
        getQueryParam('quoteId') ||
        root?.dataset?.scenarioId ||
        root?.dataset?.jobId ||
        null;

    return { baseUrl, siteKey, scenarioId, mapsKey };
}

function pickPrimaryScenario(results) {
    const scenarios = Array.isArray(results?.scenarios) ? results.scenarios : [];
    if (!scenarios.length) return null;
    if (results?.recommendedScenario) return results.recommendedScenario;
    const primaryId = results?.estimateContent?.primaryScenarioId;
    return (
        scenarios.find(s => String(s.bundleId) === String(primaryId)) ||
        scenarios.find(s => s.tier === 'recommended') ||
        scenarios[0]
    );
}

function replaceScenario(list, nextScenario) {
    if (!nextScenario) return list;
    const next = Array.isArray(list) ? [...list] : [];
    const idx = next.findIndex(item => String(item.bundleId) === String(nextScenario.bundleId));
    if (idx >= 0) {
        next[idx] = nextScenario;
    } else {
        next.push(nextScenario);
    }
    return next;
}

function buildErrorMessage(err, fallback) {
    if (!err) return fallback;
    return err.message || fallback;
}

function normalizeUkPhone(value) {
    const raw = typeof value === 'string' ? value : '';
    const digits = raw.replace(/\D/g, '');
    if (!digits) return { normalized: '', isValid: false };
    let national = digits;
    if (digits.startsWith('44')) {
        national = digits.slice(2);
    } else if (digits.startsWith('0')) {
        national = digits.slice(1);
    }
    if (national.length !== 10 || !/^[1-9]\d{9}$/.test(national)) {
        return { normalized: '', isValid: false };
    }
    return { normalized: `+44${national}`, isValid: true };
}

function toInteger(value, fallback = 0) {
    const num = Math.round(Number(value));
    return Number.isFinite(num) ? num : fallback;
}

function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}

function normalizeLatLon(location) {
    const lat = location?.lat ?? location?.latitude;
    const lon = location?.lon ?? location?.lng ?? location?.longitude;
    if (typeof lat !== 'number' || typeof lon !== 'number') return null;
    return { lat, lon };
}

function normalizePolygon(points) {
    if (!Array.isArray(points)) return null;
    const normalized = points
        .map(point => {
            if (!point) return null;
            const lat = point.lat ?? point.latitude;
            const lon = point.lon ?? point.lng ?? point.longitude;
            if (typeof lat !== 'number' || typeof lon !== 'number') return null;
            return { lat, lon };
        })
        .filter(Boolean);
    return normalized.length ? normalized : null;
}

function polygonCentroid(polygon) {
    if (!Array.isArray(polygon) || polygon.length === 0) return null;
    const total = polygon.reduce((acc, point) => ({
        lat: acc.lat + point.lat,
        lon: acc.lon + point.lon
    }), { lat: 0, lon: 0 });
    return {
        lat: total.lat / polygon.length,
        lon: total.lon / polygon.length
    };
}

function extractRoofPolygons(results) {
    const polygons = [];
    const segments = Array.isArray(results?.roof?.segments) ? results.roof.segments : [];
    segments.forEach(segment => {
        const polygon = normalizePolygon(segment?.polygon);
        if (polygon) polygons.push(polygon);
    });
    const selectionPolygon = normalizePolygon(results?.roofSelection?.polygon);
    if (selectionPolygon) polygons.push(selectionPolygon);
    return polygons;
}

function resolveRoofCenter(results, polygons) {
    const location =
        normalizeLatLon(results?.roof?.location) ||
        normalizeLatLon(results?.roofSelection?.location);
    if (location) return location;
    if (polygons?.length) return polygonCentroid(polygons[0]);
    return null;
}

function buildStaticMapUrl({
    mapsKey,
    center,
    polygons = [],
    zoom = 20,
    size = '640x360',
    scale = 2
}) {
    if (!mapsKey || !center) return null;
    const params = new URLSearchParams();
    params.set('center', `${center.lat},${center.lon}`);
    params.set('zoom', String(zoom));
    params.set('size', size);
    params.set('scale', String(scale));
    params.set('maptype', 'satellite');

    const colors = ['0xf59e0b', '0x2563eb', '0x0ea5e9', '0x10b981'];
    polygons.forEach((polygon, index) => {
        if (!polygon.length) return;
        const stroke = colors[index % colors.length];
        const fill = `${stroke}55`;
        const points = polygon.map(point => `${point.lat},${point.lon}`);
        if (points[0] !== points[points.length - 1]) {
            points.push(points[0]);
        }
        params.append(
            'path',
            `fillcolor:${fill}|color:${stroke}|weight:2|${points.join('|')}`
        );
    });

    params.set('key', mapsKey);
    return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

export function registerSolarResultsJourney() {
    if (typeof document === 'undefined') return;

    const createStore = () => {
        if (typeof window !== 'undefined' && window.__solarResultsState) {
            return window.__solarResultsState;
        }

        const state = ({
        loading: true,
        saving: false,
        saveQuoteLoading: false,
        error: null,
        saveQuoteError: null,
        saveQuoteSuccess: false,
        initialized: false,
        results: null,
        jobSaved: null,
        resultsReady: false,
        selectedPackageId: null,
        panelCount: 0,
        batteryCount: 0,
        recommendedPanelCount: null,
        recommendedBatteryCount: null,
        addOns: { ups: false, evCharger: false },
        panelCountDraft: 0,
        batteryCountDraft: 0,
        addOnsDraft: { ups: false, evCharger: false },
        financeOffers: [],
        financeApplyLink: null,
        jobId: null,
        baseUrl: null,
        siteKey: null,
        mapsKey: null,
        saveEmail: '',
        savePhone: '',
        saveApproachVariant: 'email_only',
        saveApproachRequirePhone: false,
        api: null,
        financeApi: null,
        debouncedSave: null,
        maxPanelCount: 60,
        staticMapUrl: null,
        staticMapError: null,
        financeByScenario: null,
        calModalOpen: false,
        saveQuoteModalOpen: false,
        preparingModalOpen: true,
        saveQuoteCloseVisible: false,
        adjustModalOpen: false,
        financeModalOpen: false,
        savingsModalOpen: false,
        productModalOpen: false,
        productModalBundleId: null,
        financeModalBundleId: null,
        financeModalDepositPercent: 25,
        financeModalFinance: null,
        financeModalOffers: [],
        financeSelectedOfferId: null,
        financeSelectedOfferKey: null,
        financeLoading: false,
        financeDebouncedRefresh: null,
        eventBuffer: [],
        eventFlushTimer: null,
        viewedLogged: false,

        init() {
            if (this.initialized) return;
            this.initialized = true;
            const { baseUrl, siteKey, scenarioId, mapsKey } = resolveConfig(this.$root);
            this.jobId = scenarioId;
            this.baseUrl = baseUrl;
            this.siteKey = siteKey;
            this.mapsKey = mapsKey;
            this.api = createSolarApi({ baseUrl, siteKey });
            this.financeApi = createFinanceApi({ baseUrl, siteKey });
            this.debouncedSave = debounce(() => this.saveChanges(), 350);
            this.financeDebouncedRefresh = debounce(() => this.refreshFinanceOffers(), 250);
            this.eventFlushTimer = setInterval(() => this.flushEvents(), 10_000);
            if (typeof this.$watch === 'function') {
                this.$watch('financeModalOpen', value => {
                    if (value) {
                        this.queueEvent('finance_modal_open', {
                            bundleId: this.financeModalBundleId
                        });
                    }
                });
                this.$watch('calModalOpen', value => {
                    if (value) {
                        this.queueEvent('book_call_modal_open');
                    }
                });
            }
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this.flushEvents();
                }
            });

            if (!this.jobId) {
                this.loading = false;
                this.error = 'Missing scenarioId in the URL.';
                return;
            }

            this.loadResults();
        },

        async loadResults() {
            this.loading = true;
            this.error = null;
            this.resultsReady = false;
            this.saveQuoteCloseVisible = false;
            try {
                const results = await this.api.getResults({ jobId: this.jobId });
                this.results = results;
                this.jobSaved = results?.jobSaved === true;
                this.saveApproachVariant = results?.abTest?.saveApproachVariant || 'email_only';
                this.saveApproachRequirePhone = results?.abTest?.saveApproachRequirePhone === true;
                const primaryScenario = pickPrimaryScenario(results);
                if (primaryScenario) {
                    this.selectedPackageId = primaryScenario.bundleId;
                    this.panelCount = getPanelCount(primaryScenario) ?? 1;
                    this.batteryCount = getBatteryCount(primaryScenario) ?? 0;
                    if (this.recommendedPanelCount == null) {
                        this.recommendedPanelCount = this.panelCount;
                    }
                    if (this.recommendedBatteryCount == null) {
                        this.recommendedBatteryCount = this.batteryCount;
                    }
                }
                this.panelCountDraft = this.panelCount;
                this.batteryCountDraft = this.batteryCount;
                this.addOnsDraft = { ...this.addOns };
                this.maxPanelCount = toInteger(results?.roofSummary?.maxPanels, 60) || 60;
                this.financeByScenario = results?.financeByScenario ?? null;
                const finance = normalizeFinance(results?.finance);
                this.setFinance(finance);
                const initialDeposit = Number(finance?.depositPercent);
                if (Number.isFinite(initialDeposit)) {
                    this.financeModalDepositPercent = initialDeposit;
                }

                if (!finance.offers.length && this.financeApi) {
                const fallbackFinance = await this.financeApi.getOffersForJob(this.jobId);
                this.setFinance(fallbackFinance);
            }

            const polygons = extractRoofPolygons(results);
            const center = resolveRoofCenter(results, polygons);
            this.staticMapUrl = buildStaticMapUrl({
                mapsKey: this.mapsKey,
                center,
                polygons
            });
            this.staticMapError = this.staticMapUrl ? null : 'Unable to build roof map.';
                if (!this.viewedLogged) {
                    this.queueEvent('estimate_viewed', {
                        bundleId: this.selectedPackageId
                    });
                    this.viewedLogged = true;
                }
            } catch (err) {
                this.error = buildErrorMessage(err, 'Unable to load results.');
            } finally {
                this.loading = false;
                this.resultsReady = true;
                this.saveQuoteCloseVisible = true;
                this.preparingModalOpen = false;
                if (this.jobSaved === false) {
                    this.saveQuoteModalOpen = true;
                }
            }
        },

        get currentScenario() {
            const scenarios = Array.isArray(this.results?.scenarios) ? this.results.scenarios : [];
            return scenarios.find(s => String(s.bundleId) === String(this.selectedPackageId)) || scenarios[0] || null;
        },

        get currentPanelCount() {
            return getPanelCount(this.currentScenario);
        },

        get hasViableSolarResult() {
            return Number.isFinite(this.currentPanelCount) && this.currentPanelCount > 0;
        },

        get showNoFitFallback() {
            return this.resultsReady && !this.loading && !this.error && !this.hasViableSolarResult;
        },

        get showMainResultsContent() {
            return this.resultsReady && !this.loading && !this.error && this.hasViableSolarResult;
        },

        get maxPanelsCapApplied() {
            return this.results?.roofSummary?.capApplied === true;
        },

        get maxPanelsCapValue() {
            const value = Number(this.results?.roofSummary?.configuredMaxPanels);
            return Number.isFinite(value) && value > 0 ? value : null;
        },

        get fallbackContactName() {
            return this.results?.partner?.name || this.results?.partner?.branding?.name || 'dwellow';
        },

        get fallbackContactEmail() {
            return this.results?.partner?.email || this.results?.partner?.branding?.supportEmail || 'hello@dwellow.co.uk';
        },

        get fallbackContactPhone() {
            return this.results?.partner?.phone || this.results?.partner?.branding?.supportPhone || '0330 822 2795';
        },

        get selectedPackageName() {
            return getPackageName(this.currentScenario);
        },

        get totalPrice() {
            return getTotalCost(this.currentScenario);
        },

        get financeModalScenario() {
            const scenarios = Array.isArray(this.results?.scenarios) ? this.results.scenarios : [];
            return scenarios.find(s => String(s.bundleId) === String(this.financeModalBundleId)) || null;
        },

        get productModalScenario() {
            const scenarios = Array.isArray(this.results?.scenarios) ? this.results.scenarios : [];
            return scenarios.find(s => String(s.bundleId) === String(this.productModalBundleId)) || null;
        },

        get financeModalDepositAmount() {
            const percent = Number(this.financeModalDepositPercent);
            const baseAmount =
                this.financeModalFinance?.amount ??
                getTotalCost(this.financeModalScenario) ??
                this.totalPrice ??
                null;
            if (!Number.isFinite(baseAmount) || !Number.isFinite(percent)) return null;
            return Math.round(baseAmount * (percent / 100));
        },

        get annualSavings() {
            return getAnnualSavings(this.currentScenario);
        },

        get monthlyFinance() {
            if (!Array.isArray(this.financeOffers) || !this.financeOffers.length) return null;
            const amounts = this.financeOffers
                .map(offer => offer?.monthlyAmount)
                .filter(amount => Number.isFinite(amount));
            if (!amounts.length) return null;
            return Math.min(...amounts);
        },

        get panelWattage() {
            const scenario = this.currentScenario;
            if (!scenario) return null;
            const count = getPanelCount(scenario);
            const size = scenario?.design?.systemSize_kWp ?? null;
            if (Number.isFinite(size) && Number.isFinite(count) && count > 0) {
                return Math.round((size * 1000) / count);
            }
            const panelProduct = scenario?.products?.panel ?? null;
            const fallback =
                panelProduct?.defaultWatts ??
                panelProduct?.watts ??
                panelProduct?.power_W ??
                null;
            return Number.isFinite(fallback) ? fallback : null;
        },

        get batteryChoices() {
            const scenario = this.currentScenario;
            if (scenario?.batteryOptions && Array.isArray(scenario.batteryOptions)) {
                return scenario.batteryOptions;
            }
            return [
                { count: 0, size: 'none', label: 'No battery' },
                { count: 1, size: 'small', label: 'Small battery' },
                { count: 2, size: 'medium', label: 'Medium battery' },
                { count: 3, size: 'large', label: 'Large battery' }
            ];
        },

        get isRecommendedSelection() {
            if (!this.results?.recommendedScenario) return true;
            const recId = this.results.recommendedScenario.bundleId;
            const samePackage = String(this.selectedPackageId) === String(recId);
            const recPanels = this.recommendedPanelCount ?? null;
            const recBatteries = this.recommendedBatteryCount ?? null;
            return samePackage && this.panelCount === recPanels && this.batteryCount === recBatteries;
        },

        get isRecommendedDraftSelection() {
            if (!this.results?.recommendedScenario) return true;
            const recId = this.results.recommendedScenario.bundleId;
            const samePackage = String(this.selectedPackageId) === String(recId);
            const recPanels = this.recommendedPanelCount ?? null;
            const recBatteries = this.recommendedBatteryCount ?? null;
            return samePackage && this.panelCountDraft === recPanels && this.batteryCountDraft === recBatteries;
        },

        formatCurrency(value) {
            return formatCurrencyGBP(value);
        },

        formatNumber(value) {
            return formatNumber(value);
        },

        setPackage(id) {
            this.selectedPackageId = id;
            const scenario = this.currentScenario;
            if (scenario) {
                this.panelCount = getPanelCount(scenario) ?? this.panelCount;
                this.batteryCount = getBatteryCount(scenario) ?? this.batteryCount;
            }
            this.panelCountDraft = this.panelCount;
            this.batteryCountDraft = this.batteryCount;
            this.addOnsDraft = { ...this.addOns };
        },

        incrementPanels() {
            this.setPanelCount(this.panelCountDraft + 1);
        },

        decrementPanels() {
            this.setPanelCount(this.panelCountDraft - 1);
        },

        setPanelCount(nextValue) {
            const next = clamp(toInteger(nextValue, this.panelCountDraft), 6, this.maxPanelCount || 60);
            this.panelCountDraft = next;
        },

        incrementBatteries() {
            this.setBatteryCount(this.batteryCountDraft + 1);
        },

        decrementBatteries() {
            this.setBatteryCount(this.batteryCountDraft - 1);
        },

        setBatteryCount(nextValue) {
            const next = clamp(toInteger(nextValue, this.batteryCountDraft), 0, 3);
            this.batteryCountDraft = next;
        },

        selectBatteryOption(count) {
            this.setBatteryCount(count);
        },

        toggleAddon(key) {
            this.addOnsDraft[key] = !this.addOnsDraft[key];
        },

        resetToRecommendation() {
            const rec = this.results?.recommendedScenario;
            if (!rec) return;
            this.selectedPackageId = rec.bundleId;
            this.panelCountDraft = getPanelCount(rec) ?? this.panelCountDraft;
            this.batteryCountDraft = getBatteryCount(rec) ?? this.batteryCountDraft;
            this.addOnsDraft = { ups: false, evCharger: false };
        },

        queueSave() {
            if (this.loading) return;
            if (typeof this.debouncedSave === 'function') this.debouncedSave();
        },

        async saveChanges() {
            if (!this.jobId || !this.selectedPackageId) return;
            this.saving = true;
            this.error = null;
            try {
                const res = await this.api.adjustAll({
                    jobId: this.jobId,
                    bundleId: this.selectedPackageId,
                    overrides: {
                        panelCount: this.panelCountDraft,
                        batteryCount: this.batteryCountDraft,
                        addons: this.addOnsDraft
                    },
                    depositPercent: Number.isFinite(Number(this.financeModalDepositPercent))
                        ? Number(this.financeModalDepositPercent)
                        : null
                });
                if (Array.isArray(res?.scenarios)) {
                    const nextScenarios = res.scenarios;
                    const recommendedId = this.results?.recommendedScenario?.bundleId ?? null;
                    const nextRecommended = recommendedId
                        ? nextScenarios.find(s => String(s.bundleId) === String(recommendedId))
                        : null;
                    this.results = {
                        ...this.results,
                        scenarios: nextScenarios,
                        recommendedScenario: nextRecommended ?? this.results?.recommendedScenario ?? null
                    };
                    this.financeByScenario = res?.financeByScenario ?? this.financeByScenario;
                    const selectedScenario = nextScenarios.find(
                        s => String(s.bundleId) === String(this.selectedPackageId)
                    );
                    if (selectedScenario) {
                        this.panelCount = getPanelCount(selectedScenario) ?? this.panelCount;
                        this.batteryCount = getBatteryCount(selectedScenario) ?? this.batteryCount;
                    }
                    this.panelCountDraft = this.panelCount;
                    this.batteryCountDraft = this.batteryCount;
                    this.addOnsDraft = { ...this.addOnsDraft };
                }
                if (res?.finance) {
                    this.setFinance(normalizeFinance(res.finance));
                }
                this.addOns = { ...this.addOnsDraft };
                this.adjustModalOpen = false;
            } catch (err) {
                this.error = buildErrorMessage(err, 'Unable to save changes.');
            } finally {
                this.saving = false;
            }
        },

        resetPanelsToRecommendation() {
            const rec = this.results?.recommendedScenario;
            if (!rec) return;
            this.panelCountDraft = getPanelCount(rec) ?? this.panelCountDraft;
        },

        resetBatteriesToRecommendation() {
            const rec = this.results?.recommendedScenario;
            if (!rec) return;
            this.batteryCountDraft = getBatteryCount(rec) ?? this.batteryCountDraft;
        },

        setFinance(finance) {
            this.financeOffers = Array.isArray(finance?.offers) ? finance.offers : [];
            this.financeApplyLink = finance?.applyLink ?? null;
        },

        getScenarioFinance(scenario) {
            if (!scenario) return null;
            const finance = this.financeByScenario?.[scenario.bundleId] ?? null;
            return normalizeFinance(finance);
        },

        getScenarioMonthlyFinance(scenario) {
            const finance = this.getScenarioFinance(scenario);
            if (!finance?.offers?.length) return null;
            const amounts = finance.offers
                .map(offer => offer?.monthlyAmount)
                .filter(amount => Number.isFinite(amount));
            if (!amounts.length) return null;
            return Math.min(...amounts);
        },

        openFinanceModal(bundleId) {
            this.financeModalBundleId = bundleId || this.selectedPackageId;
            const scenario = this.results?.scenarios?.find(
                s => String(s.bundleId) === String(this.financeModalBundleId)
            );
            const finance = scenario ? this.getScenarioFinance(scenario) : null;
            this.financeModalFinance = finance;
            this.financeModalOffers = finance?.offers || [];
            const displayedMonthly = this.getScenarioMonthlyFinance(scenario);
            let initialOffer =
                (Number.isFinite(displayedMonthly)
                    ? this.financeModalOffers.find(
                          offer => Math.round(offer.monthlyAmount) === Math.round(displayedMonthly)
                      )
                    : null) ||
                this.financeModalOffers[0] ||
                null;
            this.financeSelectedOfferId = initialOffer?.id || null;
            this.financeSelectedOfferKey = initialOffer
                ? this.buildFinanceOfferKey(initialOffer)
                : null;
            this.financeModalOpen = true;
            if (!Number.isFinite(Number(this.financeModalDepositPercent))) {
                this.financeModalDepositPercent = 25;
            }
            this.refreshFinanceOffers();
        },

        closeFinanceModal() {
            this.financeModalOpen = false;
        },

        openProductModal(bundleId) {
            this.productModalBundleId = bundleId || this.selectedPackageId;
            this.productModalOpen = true;
        },

        closeProductModal() {
            this.productModalOpen = false;
        },

        setFinanceDepositPercent(nextValue) {
            const next = clamp(toInteger(nextValue, this.financeModalDepositPercent), 25, 90);
            this.financeModalDepositPercent = next;
            if (typeof this.financeDebouncedRefresh === 'function') {
                this.financeDebouncedRefresh();
            }
        },

        selectFinanceOffer(offerId) {
            this.financeSelectedOfferId = offerId;
            const offer = this.financeModalOffers?.find(item => item.id === offerId) || null;
            this.financeSelectedOfferKey = offer ? this.buildFinanceOfferKey(offer) : null;
        },

        get financeSelectedOffer() {
            if (!this.financeModalOffers?.length) return null;
            if (this.financeSelectedOfferId) {
                return this.financeModalOffers.find(o => o.id === this.financeSelectedOfferId) || null;
            }
            return this.financeModalOffers[0] || null;
        },

        buildFinanceOfferKey(offer) {
            if (!offer) return null;
            const term = Number(offer.termMonths) || 0;
            const apr = Number(offer.apr) || 0;
            const interestRate = Number(offer.interestRate) || 0;
            const frequency = offer.frequency || '';
            return `${term}|${apr}|${interestRate}|${frequency}`;
        },

        async refreshFinanceOffers() {
            if (!this.jobId || !this.financeModalBundleId) return;
            this.financeLoading = true;
            try {
                const baseScenario = this.results?.scenarios?.find(
                    s => String(s.bundleId) === String(this.financeModalBundleId)
                );
                const panelCount = getPanelCount(baseScenario) ?? this.panelCount;
                const batteryCount = getBatteryCount(baseScenario) ?? this.batteryCount;
                const res = await this.api.adjustAll({
                    jobId: this.jobId,
                    bundleId: this.financeModalBundleId,
                    overrides: {
                        panelCount,
                        batteryCount,
                        addons: this.addOns
                    },
                    depositPercent: this.financeModalDepositPercent
                });
                if (res?.financeByScenario) {
                    this.financeByScenario = res.financeByScenario;
                }
                const scenario = this.results?.scenarios?.find(
                    s => String(s.bundleId) === String(this.financeModalBundleId)
                );
                const finance = scenario ? this.getScenarioFinance(scenario) : null;
                this.financeModalFinance = finance;
                this.financeModalOffers = finance?.offers || [];
                const existingId = this.financeSelectedOfferId;
                const existingKey = this.financeSelectedOfferKey;
                let nextOffer = null;
                if (existingId) {
                    nextOffer = this.financeModalOffers.find(item => item.id === existingId) || null;
                }
                if (!nextOffer && existingKey) {
                    nextOffer =
                        this.financeModalOffers.find(
                            item => this.buildFinanceOfferKey(item) === existingKey
                        ) || null;
                }
                if (!nextOffer) {
                    nextOffer = this.financeModalOffers[0] || null;
                }
                this.financeSelectedOfferId = nextOffer?.id || null;
                this.financeSelectedOfferKey = nextOffer ? this.buildFinanceOfferKey(nextOffer) : null;
            } catch (err) {
                this.error = buildErrorMessage(err, 'Unable to refresh finance.');
            } finally {
                this.financeLoading = false;
            }
        },

        async saveQuote() {
            if (this.saveQuoteLoading) return;
            const email = (this.saveEmail || '').trim();
            const phoneRaw = (this.savePhone || '').trim();
            const phoneCheck = phoneRaw ? normalizeUkPhone(phoneRaw) : { normalized: '', isValid: false };
            if (!email) {
                this.saveQuoteError = 'Enter an email address to continue.';
                return;
            }
            if (phoneRaw && !phoneCheck.isValid) {
                this.saveQuoteError = 'Enter a valid UK phone number to continue.';
                return;
            }
            if (this.saveApproachVariant === 'email_phone' && this.saveApproachRequirePhone && !phoneRaw) {
                this.saveQuoteError = 'Enter a phone number to continue.';
                return;
            }
            this.saveQuoteLoading = true;
            this.saveQuoteError = null;
            this.saveQuoteSuccess = false;
            try {
                if (typeof this.api?.saveQuote === 'function') {
                    await this.api.saveQuote({
                        jobId: this.jobId,
                        email,
                        phone: phoneCheck.isValid ? phoneCheck.normalized : ''
                    });
                }
                this.queueEvent('email_estimate', { email });
                this.saveQuoteSuccess = true;
                this.saveQuoteModalOpen = false;
                this.jobSaved = true;
            } catch (err) {
                this.saveQuoteError = buildErrorMessage(err, 'Unable to save quote.');
            } finally {
                this.saveQuoteLoading = false;
            }
        },

        queueEvent(action, meta = null) {
            if (!action) return;
            const payload = {
                action: String(action),
                meta: meta && typeof meta === 'object' ? meta : null,
                clientTimestamp: new Date().toISOString()
            };
            this.eventBuffer.push(payload);
            if (this.eventBuffer.length > 100) {
                this.eventBuffer = this.eventBuffer.slice(-100);
            }
            if (this.eventBuffer.length >= 5) {
                this.flushEvents();
            }
        },

        async flushEvents() {
            if (!this.jobId || !this.eventBuffer.length || !this.api?.logEvents) return;
            const events = this.eventBuffer.slice();
            this.eventBuffer = [];
            try {
                await this.api.logEvents({ jobId: this.jobId, events });
            } catch (err) {
                this.eventBuffer = [...events, ...this.eventBuffer].slice(-100);
            }
        }
    });

        if (typeof window !== 'undefined') {
            window.__solarResultsState = state;
        }

        return state;
    };

    if (typeof window !== 'undefined') {
        window.solarResults = createStore;
    }

    const refreshAlpine = () => {
        if (!window.Alpine || typeof window.Alpine.initTree !== 'function') return;
        const root = document.querySelector('[data-journey="solar-results"]');
        if (root) window.Alpine.initTree(root);
    };
    const ensureMounted = () => {
        if (!window.Alpine || typeof window.Alpine.initTree !== 'function') return;
        const root = document.querySelector('[data-journey="solar-results"]');
        if (root) {
            window.Alpine.initTree(root);
            return;
        }
        const observer = new MutationObserver(() => {
            const nextRoot = document.querySelector('[data-journey="solar-results"]');
            if (!nextRoot) return;
            window.Alpine.initTree(nextRoot);
            observer.disconnect();
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 5000);
    };

    const register = () => {
        if (!window.Alpine || typeof window.Alpine.data !== 'function') return;
        window.Alpine.data('solarResults', createStore);
        refreshAlpine();
    };

    if (window.Alpine) {
        register();
    } else {
        document.addEventListener('alpine:init', register);
    }

    document.addEventListener('DOMContentLoaded', refreshAlpine);
    document.addEventListener('DOMContentLoaded', ensureMounted);
}

registerSolarResultsJourney();
